import PropTypes from 'prop-types';
import { useRef, useEffect, useReducer, createContext } from 'react';

// material
import { Alert, AlertTitle } from '@material-ui/core';

// Setting
import axios from 'axios';
// moment
import moment from 'moment';
import { useLocation } from 'react-router-dom';
// OpenID-Client
import { Log, UserManager, WebStorageStateStore } from 'oidc-client';

import useAxios from '../hooks/useAxios';
import { appConfig, openIdConfig } from '../config';
// Hooks
import useIsMountedRef from '../hooks/useIsMountedRef';
import { clearLocalStorageExceptWs } from '../components/helper';

// -----------------------------------------------------------------------------
Log.logger = console;
Log.level = Log.INFO;

const SCREEN_DURATION_TIMEOUT = 30;
const SCREEN_ACTIVE_SCREEN_KEY = 'wc-active-screen';

export const userManager = new UserManager({
  authority: openIdConfig.wellknow,
  client_id: openIdConfig.clientId,
  scope: openIdConfig.scope,
  redirect_uri: openIdConfig.redirectUri,
  popup_redirect_uri: openIdConfig.popupRedirectUri,
  silent_redirect_uri: openIdConfig.silentRedirectUri,
  response_type: openIdConfig.responseType,
  post_logout_redirect_uri: openIdConfig.logoutRedirectUri,
  revokeAccessTokenOnSignout: true,
  monitorSession: false,
  automaticSilentRenew: false,
  accessTokenExpiringNotificationTime: 300,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  popupWindowTarget: 'signin_popup'
});

// -----------------------------------------------------------------------------

const initState = {
  isInitialized: false,
  isAuthenticated: false,
  error: null,
  token: null,
  user: null,
  role: null,
  type: 'identity'
};

const axiosForIdentity = axios.create({
  baseURL: appConfig.identityUri,
  withCredentials: true
});

export const OpenIdContext = createContext({
  ...initState,
  logout: null,
  signin: null
});

const stateHandler = {
  login: (state, { token, user, role }) => ({
    ...state,
    isInitialized: true,
    isAuthenticated: true,
    token,
    user,
    role,
    error: null
  }),
  sessionExpired: () => ({
    ...initState,
    isInitialized: true,
    isAuthenticated: false
  }),
  authenticatedFailed: (state, { payload }) => ({
    ...state,
    isInitialized: true,
    isAuthenticated: false,
    error: payload
  }),
  initialized: (state) => ({
    ...state,
    isInitialized: true
  })
};

const reducer = (state = initState, action) =>
  stateHandler[action.type] ? stateHandler[action.type](state, action) : state;

// -----------------------------------------------------------------------------

OpenIdProvider.propTypes = {
  children: PropTypes.node
};

export function OpenIdProvider({ children }) {
  // ? ---------------------------------- Hooks ------------------------------------
  const isMountedRef = useIsMountedRef();
  const activeScreenTime = useRef({
    ignore: true
  });
  const logoutState = useRef(false);
  const location = useLocation();
  const [state, dispatch] = useReducer(reducer, initState);
  const [get] = useAxios();

  useEffect(() => {
    // Ignore auth path for silent signin
    if (!location.pathname.startsWith('/auth') && !location.pathname.startsWith('/logout-front-channel')) {
      silentSignin();
    } else {
      dispatch({ type: 'initialized' });
    }

    async function silentSignin() {
      try {
        // Set Session Active Screen
        window.localStorage.setItem(SCREEN_ACTIVE_SCREEN_KEY, moment().format());

        userManager.events.addUserLoaded(onUserLoaded);
        userManager.events.addAccessTokenExpired(onTokenExpired);

        const user = await userManager.getUser();
        if (user && !user.expired) {
          onUserLoaded(user);
        } else {
          await userManager.signinSilent();
        }

        userManager.startSilentRenew();

        // Set Session Active Screen
        const u = user?.profile?.preferred_username;
        if (u === 'admin') activeScreenTime.current.ignore = true;
        else activeScreenTime.current.ignore = false;
      } catch (ex) {
        if (ex.error === 'login_required') {
          // user not logged in at idp, open popup dialog
          signin();
        } else {
          console.log('silent signin failed', ex);
          dispatch({
            type: 'authenticatedFailed',
            payload: (ex.data && ex.data.message) || ex.error || ex.message || 'Authenticated Failed'
          });
        }
      }
    }

    async function onUserLoaded(user) {
      if (isMountedRef.current) {
        const buActivity = localStorage.getItem('buActivity');
        if (user.access_token && buActivity === null) {
          const resp = await get('/user/authorize', { token: user.access_token });
          if (resp && resp.data) {
            const { data } = resp;

            localStorage.setItem(
              'buActivity',
              JSON.stringify(data.bu.map((o) => ({ BU: o.code, Activity: o.activity })))
            );
            localStorage.setItem(
              'ccaActivity',
              JSON.stringify(data.cca.map((o) => ({ CCA: o.code, Activity: o.activity })))
            );
            localStorage.setItem(
              'companyActivity',
              JSON.stringify(data.company.map((o) => ({ CompanyCode: o.code, Activity: o.activity })))
            );
            localStorage.setItem(
              'salesOrgActivity',
              JSON.stringify(data.salesOrg.map((o) => ({ SalesOrg: o.code, Activity: o.activity })))
            );
            localStorage.setItem(
              'plantActivity',
              JSON.stringify(data.plant.map((o) => ({ Plant: o.code, Activity: o.activity })))
            );
            localStorage.setItem(
              'shippingPointActivity',
              JSON.stringify(data.shippingPoint.map((o) => ({ ShippingPoint: o.code, Activity: o.activity })))
            );
          }
        }

        dispatch({
          type: 'login',
          token: user.access_token,
          user,
          role: user.profile.role
        });
        console.log('User loaded', user);
        localStorage.setItem('roleActivity', user.profile.role_activity);
        localStorage.setItem('isSales', user.profile.is_sales === 'Y');
        localStorage.setItem('isOutsource', user.profile.is_outsource === 'Y');
        localStorage.setItem('isWithDrawOverdue', user.profile.is_with_draw_overdue === 'Y');
        localStorage.setItem('isFactoring', user.profile.is_factoring === 'Y');
        localStorage.setItem('isSendEmailOverdue', user.profile.is_send_email_overdue === 'Y');
      }
    }

    function onTokenExpired() {
      if (isMountedRef.current) {
        clearLocalStorageExceptWs();
        dispatch({ type: 'sessionExpired' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const intervalValidSecurityStamp = setInterval(
      async () => {
        console.log('Checking security stamp...');
        try {
          const user = await userManager.getUser();
          if (user) {
            const response = await axiosForIdentity.get('/account/validate');
            if (!response?.data) {
              userManager.signoutRedirect();
            }
          }
        } catch (error) {
          console.error('Error checking security stamp:', error);
        }
      },
      1000 * 60 * 10
    ); // Check every 10 minutes

    return () => {
      if (intervalValidSecurityStamp) {
        clearInterval(intervalValidSecurityStamp);
      }
    };
  }, []);

  // Register Check Active Screen
  useEffect(() => {
    const onActiveScreen = () => {
      window.localStorage.setItem(SCREEN_ACTIVE_SCREEN_KEY, moment().format());
    };

    window.addEventListener('mousemove', onActiveScreen);
    window.addEventListener('mousedown', onActiveScreen);
    window.addEventListener('keypress', onActiveScreen);
    window.addEventListener('touchmove', onActiveScreen);
    window.addEventListener('scroll', onActiveScreen);

    const intervalId = setInterval(async () => {
      if (activeScreenTime.current.ignore || !isMountedRef.current) {
        return;
      }

      let isExpired = false;
      // Get From LocalStorage
      const lastActiveScreenValue = window.localStorage.getItem(SCREEN_ACTIVE_SCREEN_KEY);

      if (!lastActiveScreenValue) {
        isExpired = true;
      } else {
        // Check Expired
        const lastActiveScreenTime = moment(lastActiveScreenValue);
        if (
          moment()
            .add(SCREEN_DURATION_TIMEOUT * -1, 'minute')
            .isSameOrAfter(lastActiveScreenTime)
        ) {
          isExpired = true;
        }
      }

      if (isExpired) {
        activeScreenTime.current.ignore = true;
        const user = await userManager.getUser();

        // Check is authenticated or not
        if (user != null) {
          userManager.signoutRedirect();
        } else {
          dispatch({ type: 'sessionExpired' });
        }
        clearLocalStorageExceptWs();
      }
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('mousemove', onActiveScreen);
      window.removeEventListener('mousedown', onActiveScreen);
      window.removeEventListener('keypress', onActiveScreen);
      window.removeEventListener('touchmove', onActiveScreen);
      window.removeEventListener('scroll', onActiveScreen);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    if (!logoutState.current) {
      // Prevent user from double click
      logoutState.current = true;
      // Set Session Active Screen
      activeScreenTime.current.ignore = true;

      clearLocalStorageExceptWs();

      userManager.signoutRedirect();
    }
  };

  const signin = async () => {
    // Set Session Active Screen
    activeScreenTime.current.ignore = true;

    try {
      await userManager.signinPopup();
      userManager.startSilentRenew();

      // Set Session Active Screen
      activeScreenTime.current.ignore = false;
    } catch (ex) {
      dispatch({
        type: 'authenticatedFailed',
        payload: (ex.data && ex.data.message) || ex.error || ex.message || 'Authenticated Failed'
      });
    }
  };

  return (
    <OpenIdContext.Provider
      value={{
        ...state,
        signin,
        logout
      }}
    >
      {state.error && (
        <Alert sx={{ mt: 10 }} severity="error">
          <AlertTitle>Authentication Error</AlertTitle>
          {state.error}
        </Alert>
      )}
      {children}
    </OpenIdContext.Provider>
  );
}
